/**
 * Page /inscription — inscription d'une mairie/collectivité.
 *
 * Crée le 1er compte super_admin du tenant en même temps que le tenant lui-même.
 * Le compte créé devient automatiquement administrateur du tenant fraîchement créé.
 *
 * Refonte R11 (mai 2026) :
 *   - Topbar glass avec halo tech (au lieu de module-banner CSS legacy)
 *   - LogoMark Landmark accent tech + titre "Vision Pro Collectivité" + meta "Édition One For All Distribution"
 *   - Card glass avec form 5 champs (tenantName + prénom/nom + email + password)
 *   - Footer glass discret en bas avec mention RGPD / hébergement
 *
 * Logique métier 100% préservée :
 *   - useAuth().register({email, password, prenom, nom, tenantName})
 *   - Sur succès → navigate('/dashboard')
 *   - Form 5 champs, validation HTML5 + minLength 12 password
 *   - Lien "Se connecter" si compte existe déjà
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Input, Field, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { VpcMairieIcon } from '@/components/ui/VpcMairieIcon';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/lib/auth-context';
import { ApiException } from '@/lib/api';
import { Landmark, AlertCircle } from 'lucide-react';

// Topbar style partagé (halo tech)
const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

export function InscriptionCollectivitePage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    email: '',
    password: '',
    prenom: '',
    nom: '',
    tenantName: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function update<K extends keyof typeof form>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await register(form);
      navigate('/dashboard');
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error);
      } else {
        setError('Une erreur est survenue, veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen flex-col text-ink" id="main-content">
      {/* Topbar glass */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 border-b border-border px-4 py-3 backdrop-blur-glass sm:px-6"
        style={TOPBAR_BG}
      >
        <LogoMark icon={VpcMairieIcon} accent="tech" size="md" aria-label="Vision Pro Collectivité" />
        <div className="min-w-0">
          <SectionEyebrow>Édition One For All Distribution</SectionEyebrow>
          <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
            Vision Pro Collectivité
          </div>
        </div>
        <div className="ml-auto">
          <ThemeToggle />
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center px-4 py-8 sm:py-12 animate-fade-in">
        <div className="w-full max-w-lg space-y-6">
          <div className="text-center">
            <LogoMark icon={Landmark} accent="tech" size="lg" className="mx-auto" />
            <SectionEyebrow as="div" className="mt-3">Démarrage</SectionEyebrow>
            <h1 className="mt-1 font-display text-2xl sm:text-3xl font-semibold tracking-r-tight text-ink">
              Démarrer pour votre collectivité
            </h1>
            <p className="mt-2 text-sm text-ink-muted leading-relaxed">
              Création du compte administrateur — vous pourrez ensuite inviter vos agents et élus.
            </p>
          </div>

          <Card variant="glass" padded>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {error && (
                <Alert variant="error" icon={AlertCircle}>{error}</Alert>
              )}

              <Field
                label="Nom officiel de la collectivité"
                htmlFor="tenantName"
                required
                hint="Tel qu'il apparaîtra dans les en-têtes et notifications"
              >
                <Input
                  id="tenantName"
                  value={form.tenantName}
                  onChange={(e) => update('tenantName', e.target.value)}
                  placeholder="Mairie de Saint-Nazaire-sur-Charente"
                  required
                  autoFocus
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Prénom" htmlFor="prenom" required>
                  <Input
                    id="prenom"
                    autoComplete="given-name"
                    value={form.prenom}
                    onChange={(e) => update('prenom', e.target.value)}
                    required
                  />
                </Field>
                <Field label="Nom" htmlFor="nom" required>
                  <Input
                    id="nom"
                    autoComplete="family-name"
                    value={form.nom}
                    onChange={(e) => update('nom', e.target.value)}
                    required
                  />
                </Field>
              </div>

              <Field label="Email professionnel" htmlFor="email" required>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="prenom.nom@mairie.fr"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  required
                />
              </Field>

              <Field
                label="Mot de passe"
                htmlFor="password"
                hint="Au moins 12 caractères. Pas de contrainte de complexité forcée (NIST 800-63B)."
                required
              >
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  minLength={12}
                  required
                />
              </Field>

              <Button type="submit" loading={loading} className="w-full" size="lg">
                Créer le compte administrateur
              </Button>
            </form>
          </Card>

          <p className="text-center text-sm text-ink-muted">
            Vous avez déjà un compte ?{' '}
            <Link
              to="/connexion"
              className="rounded-r-md px-1 font-medium text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              Se connecter
            </Link>
          </p>
        </div>
      </div>

      <footer className="border-t border-border bg-surface/40 backdrop-blur-glass px-4 py-3 text-center text-xs text-ink-subtle">
        Vision Pro Collectivité (VPC) — édité par One For All Distribution · hébergement souverain UE · RGPD conforme
      </footer>
    </main>
  );
}
